import { useState, useEffect, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import OrganizerLayout from '../components/OrganizerLayout';
import { supabase } from '@/lib/supabase';

type ReportType = 'teams' | 'participants' | 'college' | 'department' | 'domain' | 'paid';
type ExportFormat = 'pdf' | 'excel' | 'csv';

const reportTypes: { key: ReportType; label: string }[] = [
  { key: 'teams', label: 'Team List' }, { key: 'participants', label: 'Participant List' },
  { key: 'college', label: 'College-wise' }, { key: 'department', label: 'Department-wise' },
  { key: 'domain', label: 'Domain-wise' }, { key: 'paid', label: 'Paid Teams' },
];

const exportFormats: { key: ExportFormat; label: string; icon: string }[] = [
  { key: 'pdf', label: 'PDF', icon: 'ri-file-pdf-line' },
  { key: 'excel', label: 'Excel', icon: 'ri-file-excel-line' },
  { key: 'csv', label: 'CSV', icon: 'ri-file-text-line' },
];

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const csvContent = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getDateStr() {
  return new Date().toISOString().split('T')[0];
}

export default function OrganizerReports() {
  const [selectedReport, setSelectedReport] = useState<ReportType>('teams');
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('pdf');
  const [reportHeaders, setReportHeaders] = useState<string[]>([]);
  const [reportRows, setReportRows] = useState<string[][]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadMsg, setDownloadMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setDownloadMsg('');
    try {
      let headers: string[] = [];
      let rows: string[][] = [];

      switch (selectedReport) {
        case 'teams': {
          const { data: teams } = await supabase.from('teams').select('registration_id,team_name,college_name,department,city,status,created_at').order('created_at', { ascending: false });
          headers = ['Reg ID', 'Team Name', 'College', 'Dept', 'City', 'Status'];
          rows = (teams || []).map((t) => [t.registration_id, t.team_name, t.college_name, t.department, t.city, t.status]);
          break;
        }
        case 'participants': {
          const { data: p } = await supabase.from('participants').select('full_name,email,department,year_of_study,course_type,team_id').order('full_name');
          const { data: t } = await supabase.from('teams').select('id,team_name,college_name');
          const teamMap: Record<string, string> = {};
          (t || []).forEach((tm) => { teamMap[tm.id] = `${tm.team_name} (${tm.college_name})`; });
          headers = ['Name', 'Email', 'Department', 'Year', 'Course Type', 'Team'];
          rows = (p || []).map((pp) => [pp.full_name, pp.email, pp.department, pp.year_of_study, pp.course_type, teamMap[pp.team_id] || '']);
          break;
        }
        case 'college': {
          const { data: teams } = await supabase.from('teams').select('college_name');
          const counts: Record<string, number> = {};
          (teams || []).forEach((t) => { counts[t.college_name] = (counts[t.college_name] || 0) + 1; });
          headers = ['College', 'Teams'];
          rows = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([n, c]) => [n, String(c)]);
          break;
        }
        case 'department': {
          const { data: p } = await supabase.from('participants').select('department');
          const counts: Record<string, number> = {};
          (p || []).forEach((pp) => { counts[pp.department] = (counts[pp.department] || 0) + 1; });
          headers = ['Department', 'Participants'];
          rows = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([n, c]) => [n, String(c)]);
          break;
        }
        case 'domain': {
          const { data: ps } = await supabase.from('problem_statements').select('id,title,domain_id').order('title');
          const { data: domains } = await supabase.from('domains').select('id,name');
          const { data: teams } = await supabase.from('teams').select('selected_problem_statement_id').not('selected_problem_statement_id', 'is', null);
          
          const domainMap: Record<string, string> = {};
          (domains || []).forEach((d) => { domainMap[d.id] = d.name; });
          
          const teamCounts: Record<string, number> = {};
          (teams || []).forEach((t) => {
            const psid = t.selected_problem_statement_id!;
            teamCounts[psid] = (teamCounts[psid] || 0) + 1;
          });
          
          headers = ['Problem Statement', 'Domain', 'Teams'];
          rows = (ps || []).map((p) => {
            const domainName = domainMap[p.domain_id || ''] || 'Unknown';
            return [p.title, domainName, String(teamCounts[p.id] || 0)];
          }).sort((a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]));
          break;
        }
        case 'paid': {
          const { data: pays } = await supabase.from('payments').select('team_id,amount,status,created_at').eq('status', 'paid').order('created_at', { ascending: false });
          const { data: teams } = await supabase.from('teams').select('id,registration_id,team_name,college_name');
          const teamMap: Record<string, string> = {};
          (teams || []).forEach((tm) => { teamMap[tm.id] = `${tm.registration_id} - ${tm.team_name} (${tm.college_name})`; });
          headers = ['Team', 'Amount', 'Paid On'];
          rows = (pays || []).map((p) => [teamMap[p.team_id] || p.team_id, `₹${(p.amount || 0) / 100}`, new Date(p.created_at).toLocaleDateString('en-IN')]);
          break;
        }
      }

      setReportHeaders(headers);
      setReportRows(rows);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, [selectedReport]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = () => {
    if (reportHeaders.length === 0) return;
    const date = getDateStr();
    const label = reportTypes.find((r) => r.key === selectedReport)?.label || selectedReport;
    const filename = `${label.replace(/\s+/g, '_')}_${date}`;

    if (selectedFormat === 'csv') {
      downloadCSV(`${filename}.csv`, reportHeaders, reportRows);
      setDownloadMsg(`Downloaded: ${filename}.csv`);
      setTimeout(() => setDownloadMsg(''), 3000);
      return;
    }

    if (selectedFormat === 'excel') {
      downloadCSV(`${filename}.xls`, reportHeaders, reportRows);
      setDownloadMsg(`Downloaded: ${filename}.xls`);
      setTimeout(() => setDownloadMsg(''), 3000);
      return;
    }

    if (selectedFormat === 'pdf') {
      const printWindow = window.open('', '_blank');
      if (!printWindow) { setDownloadMsg('Allow popups for PDF'); return; }
      printWindow.document.write(`<html><head><title>${label} - ${date}</title><style>body{font-family:Arial;padding:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:6px;text-align:left;font-size:11px}th{background:#f5f5f5}h2{color:#333}</style></head><body><h2>${label}</h2><p>Generated: ${date}</p><table><thead><tr>${reportHeaders.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${reportRows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`);
      printWindow.document.close();
      printWindow.print();
      setDownloadMsg('PDF printing...');
      setTimeout(() => setDownloadMsg(''), 3000);
    }
  };

  const renderPreview = () => {
    if (loading) return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-secondary-400 border-t-transparent rounded-full animate-spin"></div></div>;
    if (reportHeaders.length === 0) return <p className="text-sm text-foreground-500 p-4">No data available</p>;
    return (
      <table className="w-full text-sm">
        <thead><tr className="border-b border-background-200/70">{reportHeaders.map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-foreground-500">{h}</th>)}</tr></thead>
        <tbody>{reportRows.map((r, i) => <tr key={i} className="border-b border-background-100"><td className="px-3 py-2 text-xs text-foreground-600">{r[0]}</td>{r.slice(1).map((c, j) => <td key={j} className="px-3 py-2 text-xs text-foreground-600">{c}</td>)}</tr>)}</tbody>
      </table>
    );
  };

  return (
    <AuthGuard requiredRole="organizer">
      <OrganizerLayout title="Reports" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/organizer' }, { label: 'Reports' }]}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Report Type</h3>
              <div className="space-y-1">
                {reportTypes.map((r) => (
                  <button key={r.key} onClick={() => setSelectedReport(r.key)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${selectedReport === r.key ? 'bg-secondary-100 text-secondary-700 font-semibold' : 'text-foreground-600 hover:bg-background-100'}`}>{r.label}</button>
                ))}
              </div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Export Format</h3>
              <div className="grid grid-cols-3 gap-2">
                {exportFormats.map((f) => (
                  <button key={f.key} onClick={() => setSelectedFormat(f.key)} className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-colors cursor-pointer ${selectedFormat === f.key ? 'border-secondary-300 bg-secondary-50 text-secondary-700' : 'border-background-200 bg-background-50 text-foreground-500 hover:bg-background-100'}`}>
                    <div className="w-5 h-5 flex items-center justify-center"><i className={`${f.icon} text-lg`}></i></div>
                    <span className="text-xs font-medium">{f.label}</span>
                  </button>
                ))}
              </div>
              <button onClick={handleDownload} className="w-full mt-4 px-4 py-2.5 rounded-lg bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-download-line"></i>Download {selectedFormat.toUpperCase()}
              </button>
              {downloadMsg && <p className="text-xs text-accent-600 mt-2">{downloadMsg}</p>}
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
              <div className="border-b border-background-200/70 px-5 py-3 flex items-center justify-between">
                <h3 className="font-heading text-sm font-bold text-foreground-950">Preview</h3>
                <button onClick={fetchData} className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer">
                  <i className="ri-refresh-line mr-1"></i>Refresh
                </button>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">{renderPreview()}</div>
            </div>
          </div>
        </div>
      </OrganizerLayout>
    </AuthGuard>
  );
}